import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWishlist } from "../../context/WishlistContext";
import { useCart } from "../../context/CartContext";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import "./clientTheme.css";
import "./ProductCard.css";

const NEW_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

function getMainImage(product) {
  if (!product.images || product.images.length === 0) return null;
  return product.images.find((img) => img.isMain) || product.images[0];
}

function isNewProduct(product) {
  if (!product.createdAt) return false;
  return Date.now() - new Date(product.createdAt).getTime() < NEW_THRESHOLD_MS;
}

function HeartIcon({ filled }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export default function ProductCard({ product }) {
  const { t } = useTranslation(["productCard", "common"]);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { addItem } = useCart();
  const [activeIndex, setActiveIndex] = useState(0);
  const gallery = useMemo(() => {
    const valid = (product.images || []).filter((img) => !!img?.imageUrl);
    const main = valid.find((img) => img.isMain) || valid[0];
    return main ? [main, ...valid.filter((img) => img !== main)] : [];
  }, [product.images]);
  const image = gallery[activeIndex] || getMainImage(product);
  const visibleDots = Math.min(gallery.length, 5);

  // Desktop: sweeping the pointer across the photo previews the other images
  // (each dots-wide slice of the image maps to one photo); leaving resets to the main one.
  function handleImageMouseMove(e) {
    if (gallery.length < 2) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const index = Math.min(visibleDots - 1, Math.max(0, Math.floor(ratio * visibleDots)));
    if (index !== activeIndex) setActiveIndex(index);
  }
  const hasDiscount = product.discountPrice != null && product.discountPrice < product.price;
  const discountPercent =
    product.discountPercent ?? (hasDiscount ? Math.round((1 - product.discountPrice / product.price) * 100) : 0);
  const wishlisted = isWishlisted(product.productId);
  const outOfStock = (product.stockQuantity ?? 0) <= 0;
  const activeVariants = (product.variants || []).filter((v) => v.isActive);
  const hasMultipleVariants = activeVariants.length > 1;
  const authenticityLabel = pickLocalizedName(
    language,
    product.authenticityValue,
    product.authenticityValueArabic,
    product.authenticityValueHebrew,
  );

  function handleWishlistClick(e) {
    e.stopPropagation();
    toggleWishlist(product.productId);
  }

  function handleCartClick(e) {
    e.stopPropagation();
    if (hasMultipleVariants) {
      navigate(`/product/${product.productId}`);
      return;
    }
    addItem(product.productId);
  }

  return (
    <div className="product-card" onClick={() => navigate(`/product/${product.productId}`)}>
      <div
        className="product-card-image-wrap"
        onMouseMove={handleImageMouseMove}
        onMouseLeave={() => setActiveIndex(0)}
      >
        {image ? (
          <img src={image.imageUrl} alt={product.name} className="product-card-image" />
        ) : (
          <div className="product-card-image product-card-image-placeholder" />
        )}

        {outOfStock && (
          <div className="product-card-out-of-stock-wrap">
            <span className="product-card-out-of-stock-text">{t("common:outOfStock")}</span>
          </div>
        )}

        {!outOfStock && hasDiscount && (
          <span className="product-card-badge product-card-badge-discount">-{discountPercent}%</span>
        )}
        {!outOfStock && !hasDiscount && isNewProduct(product) && (
          <span className="product-card-badge product-card-badge-new">{t("newBadge")}</span>
        )}
        {!outOfStock && authenticityLabel && (
          <span className="product-card-badge product-card-badge-authenticity">{authenticityLabel}</span>
        )}
        {!outOfStock && product.requiresSpecialDelivery && (
          <span className="product-card-badge product-card-badge-special-delivery">
            {t("specialDeliveryBadge")}
          </span>
        )}
        {gallery.length > 1 && (
          <div className="product-card-dots" aria-hidden="true">
            {Array.from({ length: visibleDots }).map((_, i) => (
              <span
                key={i}
                className={`product-card-dot${i === activeIndex ? " active" : ""}${
                  i === 4 && gallery.length > 5 ? " small" : ""
                }`}
              />
            ))}
          </div>
        )}
        <button
          type="button"
          className={`product-card-wishlist-button ${wishlisted ? "active" : ""}`}
          aria-label={wishlisted ? t("removeFromWishlist") : t("addToWishlist")}
          onClick={handleWishlistClick}
        >
          <HeartIcon filled={wishlisted} />
        </button>
      </div>

      <div className={`product-card-body${outOfStock ? " product-card-body-disabled" : ""}`}>
        <div className="product-card-name">{product.name}</div>

        <div className="product-card-price-row">
          {hasDiscount ? (
            <>
              <span className="product-card-price-discount">{product.discountPrice.toFixed(2)} ₪</span>
              <span className="product-card-price-original">{product.price.toFixed(2)} ₪</span>
            </>
          ) : (
            <span className="product-card-price">{product.price.toFixed(2)} ₪</span>
          )}
        </div>

        {product.rating > 0 && (
          <div className="product-card-rating">
            <span className="product-card-rating-star">★</span> {product.rating.toFixed(1)}
          </div>
        )}
      </div>

      <button
        type="button"
        className="product-card-cart-button"
        aria-label={t("common:addToCart")}
        disabled={outOfStock}
        onClick={handleCartClick}
      >
        <PlusIcon />
      </button>
    </div>
  );
}
